import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOfferCalculations,
  updateOfferCalculation,
  createOfferCalculation,
  deleteOfferCalculation,
  fetchOfferCalculation,
} from "../api/offer-calculation";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  calculation: {},
  totalCount: 0,
  currentCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 50,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  active: null,
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/deleteOfferCalculationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteOfferCalculation(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_offer_calculation");
    }
  }
);

export const createOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/createOfferCalculationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      // Soft envelopes must stay raw (status/errors) for kalkyle conflict detection.
      const response = await createOfferCalculation(payload);
      if (isFailedCalculationUpdate(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "create_offer_calculation");
    }
  }
);

function isFailedCalculationUpdate(response) {
  if (!response) return true;
  const status = response.response?.status ?? response.status;
  if (typeof status === "number" && status >= 400) return true;
  if (response.success === false) return true;
  if (response.error) return true;
  const errors = response.errors;
  if (Array.isArray(errors) && errors.length > 0) return true;
  if (errors && typeof errors === "object" && Object.keys(errors).length > 0) {
    return true;
  }
  return false;
}

export const updateOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/updateOfferCalculationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      // Soft envelopes must stay raw (status/errors) for kalkyle conflict detection.
      const response = await updateOfferCalculation(payload);
      if (isFailedCalculationUpdate(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "update_offer_calculation");
    }
  }
);

export const getOfferCalculationsAsync = createAsyncThunk(
  "offerCalculations/getOfferCalculationsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchOfferCalculations(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_offer_calculations"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_offer_calculations");
    }
  }
);

export const getOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/getOfferCalculationAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchOfferCalculation(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "get_offer_calculation"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_offer_calculation");
    }
  }
);

const offerCalculationSlice = createSlice({
  name: "offerCalculations",
  initialState,
  reducers: { adoptCalculationSnapshot(state, action) { state.calculation = action.payload; } },
  extraReducers: (builder) => {
    builder
      .addCase(getOfferCalculationsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getOfferCalculationsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
      })
      .addCase(getOfferCalculationsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente tilbudskalkylene."
        );
      })
      .addCase(updateOfferCalculationAsync.pending, (state) => {
        state.isSaving = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.calculation = action.payload;
          draftState.isSaving = false;
        });
      })
      .addCase(updateOfferCalculationAsync.rejected, (state, action) => {
        state.isSaving = false;
        state.error = false;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere tilbudskalkylen."
        );
      })
      .addCase(createOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createOfferCalculationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette tilbudskalkylen."
        );
      })
      .addCase(deleteOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg;
          if (id != null) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteOfferCalculationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette tilbudskalkylen."
        );
      })
      .addCase(getOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.calculation = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getOfferCalculationAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente tilbudskalkylen."
        );
      });
  },
});

export const { reducer } = offerCalculationSlice;

export default offerCalculationSlice;

export const { adoptCalculationSnapshot } = offerCalculationSlice.actions;
